import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { readExcelRows } from '../../api/parseCommercialInvoiceFile'
import { explainSageError } from '../../lib/sageErrorMessage'
import SageFailureDialog from '../../components/SageFailureDialog'
import {
  useSyncToSage, buildSageReference,
  type SageCsvRow, type SageCsvRowResult, type SageDepartmentCategory,
  type SageBatchProgress, type SageIncompleteBatch,
} from '../../hooks/useSyncToSage'

// Z67.18: mirrors the backend's overseas check (supabase/functions/_shared/sage.ts,
// isCustomerOverseas) for PREVIEW purposes only -- the actual sync always re-derives this
// server-side (falling back to Sage's own contact record when a row's Customer Reference
// doesn't match one of our customers), this is just so Gary can see the conversion coming
// before he hits Sync.
const DOMESTIC_COUNTRY_CODES = new Set(['GB', 'IE'])

function vatRateDisplay(row: SageCsvRow, isOverseas: boolean): string {
  const csvValue = (row['VAT Rate'] || '').trim()
  if (!isOverseas || /^zero(\s*rated)?$/i.test(csvValue)) return csvValue || '—'
  return csvValue ? `${csvValue} → Zero Rated (overseas)` : 'Zero Rated (overseas)'
}

// Z67.20: mirrors the backend's OVERSEAS_LEDGER_ACCOUNT_OVERRIDE (only 4002 "UK Sales" gets
// swapped to 4004 "Overseas sales" -- any other CSV code is left alone).
const OVERSEAS_LEDGER_ACCOUNT_OVERRIDE: Record<string, string> = { '4002': '4004' }

function ledgerAccountDisplay(row: SageCsvRow, isOverseas: boolean): string {
  const csvValue = (row['Ledger Account'] || '').trim()
  const override = isOverseas ? OVERSEAS_LEDGER_ACCOUNT_OVERRIDE[csvValue] : undefined
  return override ? `${csvValue} → ${override} (overseas)` : (csvValue || '—')
}

// Z65 addenda: BoxTop's own invoice export (e.g. HS000055.csv) synced via the API, since Gary
// says Sage's own CSV importer doesn't support overseas (non-UK) companies at all. Parsed
// client-side with `xlsx` (via readExcelRows, already used elsewhere in this repo for
// spreadsheet/CSV parsing -- it handles quoted fields correctly, unlike a hand-rolled splitter),
// not posted as a raw file -- matches this codebase's existing convention (every edge function
// takes JSON, none accept a raw file body).
const EXPECTED_HEADERS = ['Type', 'Customer Reference', 'Date', 'Customer Name', 'Reference', 'Ledger Account', 'Details', 'Net', 'VAT Rate', 'VAT', 'Total'] as const

// Z67: confirmed live -- every row failed with "Could not parse Date '46121'". `readExcelRows`
// (via `xlsx`/SheetJS, no `cellDates`/`raw:false` option) returns a raw NUMBER for a genuinely
// date-typed cell rather than formatted text, which the old `String(raw[idx])` cast turned
// straight into "46121" with no way to tell it apart from a real 5-digit text value downstream.
// This is the actual cell-type information from SheetJS -- convert it directly here (the ROOT
// fix), rather than only pattern-matching a numeric-looking STRING after the type information is
// already lost (the backend's parseCsvDate does that too, as defense-in-depth, but catching it
// here means the preview table itself shows a real date instead of "46121"). Not touched:
// `readExcelRows` itself, which is shared with unrelated commercial-invoice parsing elsewhere in
// this repo -- scoped this fix to the Date column of this one feature instead of changing that
// shared function's SheetJS options for every caller.
//
// Same epoch as the backend's excelSerialToIsoDate (_shared/sage.ts) -- Dec 30 1899, the
// standard two-day-shifted epoch every implementation uses to compensate for Excel's fake 1900
// leap day. Verified against the well-known anchor 45658 = 2025-01-01 before trusting it.
function excelSerialToIsoDate(serial: number): string {
  const ms = Date.UTC(1899, 11, 30) + serial * 86400000
  return new Date(ms).toISOString().substring(0, 10)
}

function formatDateCell(cell: unknown): string {
  if (cell instanceof Date) {
    return `${cell.getUTCFullYear()}-${String(cell.getUTCMonth() + 1).padStart(2, '0')}-${String(cell.getUTCDate()).padStart(2, '0')}`
  }
  if (typeof cell === 'number' && cell >= 40000 && cell <= 60000) {
    return excelSerialToIsoDate(cell)
  }
  return String(cell ?? '').trim()
}

// Z68: optional column -- NOT part of BoxTop's fixed export (not in EXPECTED_HEADERS), so its
// absence never triggers the "missing expected headers" error. Accepts either header name Gary
// might use.
function findDepartmentColumnIndex(headerRow: string[]): number {
  const idx = headerRow.findIndex(h => h.toLowerCase() === 'department')
  if (idx >= 0) return idx
  return headerRow.findIndex(h => h.toLowerCase() === 'analysis code')
}

// Z67.19: optional -- overrides the fixed "FREIGHT FORWARDING SALES" line description (see
// FIXED_LINE_ITEM_DESCRIPTION in _shared/sage.ts) with a specific service type for this row.
function findServiceTypeColumnIndex(headerRow: string[]): number {
  return headerRow.findIndex(h => h.toLowerCase() === 'service type')
}

// Z67.23: Sage renamed this column "CustomerRef" in its Bulk Import CSV format (was "Additional
// Reference", which itself needed typo-tolerant matching -- Z67.22.2/.3 -- since Gary typed it
// by hand; "CustomerRef" is Sage's own fixed header now, so an exact case-insensitive match is
// enough). Still not part of BoxTop's fixed export (EXPECTED_HEADERS), so its absence never
// triggers the "missing expected headers" error.
//
// Backward-compat fallback: a CSV built before Sage's rename (or a template someone's still
// reusing) may still carry the old "Additional Reference"/"Addtional Reference"/"Add Ref" header
// -- accept those too, same typo-tolerant patterns Z67.22.2/.3 originally matched, so an old file
// doesn't silently lose its third reference segment just because the column wasn't renamed.
const ADDITIONAL_REFERENCE_HEADER_RE = /^add[i]?t[i]?onal\s*ref(erence)?$/
const ADD_REF_SHORTHAND_RE = /^add\s*ref(erence)?$/
function findCustomerRefColumnIndex(headerRow: string[]): number {
  const exact = headerRow.findIndex(h => h.trim().toLowerCase() === 'customerref')
  if (exact >= 0) return exact
  return headerRow.findIndex(h => {
    const s = h.trim().toLowerCase()
    return ADDITIONAL_REFERENCE_HEADER_RE.test(s) || ADD_REF_SHORTHAND_RE.test(s)
  })
}

export function parseRows(grid: unknown[][]): { rows: SageCsvRow[]; skipped: number } {
  if (grid.length === 0) return { rows: [], skipped: 0 }
  const headerRow = (grid[0] || []).map(h => String(h ?? '').trim())
  const colIndex = (name: string) => headerRow.findIndex(h => h.toLowerCase() === name.toLowerCase())
  const indices = Object.fromEntries(EXPECTED_HEADERS.map(h => [h, colIndex(h)])) as Record<string, number>
  const departmentIdx = findDepartmentColumnIndex(headerRow)
  const serviceTypeIdx = findServiceTypeColumnIndex(headerRow)
  const customerRefIdx = findCustomerRefColumnIndex(headerRow)

  const rows: SageCsvRow[] = []
  let skipped = 0
  for (const raw of grid.slice(1)) {
    if (!raw || raw.every(c => c === undefined || c === null || String(c).trim() === '')) continue
    const get = (name: string) => {
      const idx = indices[name]
      return idx >= 0 ? String(raw[idx] ?? '').trim() : ''
    }
    const type = get('Type')
    if (!type) { skipped++; continue }
    const dateIdx = indices['Date']
    rows.push({
      Type: type,
      'Customer Reference': get('Customer Reference'),
      Date: dateIdx >= 0 ? formatDateCell(raw[dateIdx]) : '',
      'Customer Name': get('Customer Name'),
      Reference: get('Reference'),
      'Ledger Account': get('Ledger Account'),
      Details: get('Details'),
      Net: get('Net'),
      'VAT Rate': get('VAT Rate'),
      VAT: get('VAT'),
      Total: get('Total'),
      Department: departmentIdx >= 0 ? String(raw[departmentIdx] ?? '').trim() : '',
      'Service Type': serviceTypeIdx >= 0 ? String(raw[serviceTypeIdx] ?? '').trim() : '',
      CustomerRef: customerRefIdx >= 0 ? String(raw[customerRefIdx] ?? '').trim() : '',
    })
  }
  return { rows, skipped }
}

export default function SageBulkImport() {
  const { bulkImportCsv, retryBatch, resumeBatch, listIncompleteBatches, dismissBatch, dismissErrors, exportFailuresCsv, fetchDepartmentCategories } = useSyncToSage()
  const [filename, setFilename] = useState<string | null>(null)
  const [rows, setRows] = useState<SageCsvRow[]>([])
  const [skippedCount, setSkippedCount] = useState(0)
  const [parseError, setParseError] = useState<string | null>(null)
  const [syncing, setSyncing] = useState(false)
  const [batchId, setBatchId] = useState<string | null>(null)
  const [results, setResults] = useState<SageCsvRowResult[]>([])
  // Opens when a run finishes with any failed row. A modal rather than a banner: in a 234-row
  // list the failed rows can sit hundreds of pixels below the fold and go unseen (HS000072:
  // rows 210/211 failed, nobody scrolled to them).
  const [showFailureDialog, setShowFailureDialog] = useState(false)
  // Z73.22: "正在同步 第 2/3 批 · 已完成 168/239 条" -- updated on every chunk round trip, not
  // just at the end, so a large batch never runs silently.
  const [progress, setProgress] = useState<SageBatchProgress | null>(null)
  // Z73.22: batches left over from a browser close/crash mid-sync -- offered on mount so Gary
  // doesn't have to re-upload the CSV to finish an interrupted run.
  const [incompleteBatches, setIncompleteBatches] = useState<SageIncompleteBatch[]>([])
  const [dismissingId, setDismissingId] = useState<string | null>(null)
  // Z68: live from Sage's "Department" analysis type -- empty array (dropdown just doesn't
  // render) if Gary hasn't set that up in Sage, rather than a hardcoded '01'/'02'/'03' list
  // that'd silently drift from whatever's actually configured there.
  const [departmentCategories, setDepartmentCategories] = useState<SageDepartmentCategory[]>([])
  const [defaultDepartment, setDefaultDepartment] = useState('')
  // Z67.18: Customer Reference -> overseas? (country outside GB/IE), for the VAT Rate
  // auto-conversion preview below. Populated once per parsed file.
  const [overseasByReference, setOverseasByReference] = useState<Record<string, boolean>>({})

  useEffect(() => {
    fetchDepartmentCategories().then(setDepartmentCategories)
    listIncompleteBatches().then(setIncompleteBatches)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const references = Array.from(new Set(rows.map(r => r['Customer Reference']).filter(Boolean)))
    if (references.length === 0) { setOverseasByReference({}); return }
    supabase.from('customers').select('code, country_code').in('code', references).then(({ data }) => {
      const map: Record<string, boolean> = {}
      for (const c of data || []) {
        if (c.country_code) map[c.code] = !DOMESTIC_COUNTRY_CODES.has(c.country_code.toUpperCase())
      }
      setOverseasByReference(map)
    })
  }, [rows])

  const handleFile = async (file: File) => {
    setParseError(null)
    setResults([])
    setBatchId(null)
    setFilename(file.name)
    try {
      // Z67.13: raw:true -- see readExcelRows' own comment. Without it, an ambiguous CSV date
      // like "04/09/2026" (UK: 4 September) gets silently reinterpreted as US MM/DD (September
      // -> read as "4/9" -> April 9) before formatDateCell ever runs.
      const grid = await readExcelRows(file, { raw: true })
      const { rows: parsed, skipped } = parseRows(grid)
      setRows(parsed)
      setSkippedCount(skipped)
      if (parsed.length === 0) setParseError('No usable rows found -- check the file has the expected headers (Type, Customer Reference, Date, Customer Name, Reference, Ledger Account, Details, Net, VAT Rate, VAT, Total).')
    } catch (err: any) {
      setRows([])
      setParseError(err?.message || 'Failed to read file')
    }
  }

  const removeRow = (idx: number) => setRows(prev => prev.filter((_, i) => i !== idx))

  // Z68: fills in the per-upload default Department for any row whose CSV column was blank.
  // Computed (never mutated into `rows`) so the preview table and what's actually sent always
  // agree, and changing the dropdown after parsing updates both immediately.
  const effectiveRows = rows.map(r => ({ ...r, Department: (r.Department || defaultDepartment || '').trim() }))

  const handleSyncAll = async () => {
    setSyncing(true)
    setProgress(null)
    try {
      const result = await bulkImportCsv(effectiveRows, filename || undefined, setProgress)
      if (!result.success && !result.batchId) {
        setParseError(result.error || 'Import failed')
        return
      }
      setBatchId(result.batchId || null)
      setResults(result.results || [])
      if ((result.results || []).some(r => !r.success)) setShowFailureDialog(true)
      if (result.error) setParseError(`Batch stopped early: ${result.error} -- reopen this page to resume from where it left off.`)
    } finally {
      setSyncing(false)
      setProgress(null)
    }
  }

  const handleResume = async (batch: SageIncompleteBatch) => {
    setSyncing(true)
    setProgress(null)
    setBatchId(batch.id)
    setFilename(batch.filename)
    try {
      const result = await resumeBatch(batch, setProgress)
      setResults(result.results || [])
      if ((result.results || []).some(r => !r.success)) setShowFailureDialog(true)
      if (result.error) setParseError(`Batch stopped early: ${result.error} -- reopen this page to resume again.`)
      setIncompleteBatches(await listIncompleteBatches())
    } finally {
      setSyncing(false)
      setProgress(null)
    }
  }

  // Z73.24: names the file, the pending count, and a preview of the actual rows so Gary is
  // confirming against something concrete, not a blind click -- one Dismiss already clears every
  // pending row in the batch (dismissBatch matches on batch_id, not a single row).
  const buildDismissConfirmMessage = (batch: SageIncompleteBatch): string => {
    const previewLines = batch.pendingPreview
      .map(p => `  Row ${p.row_index + 1} -- ${p.reference || '(no reference)'}`)
      .join('\n')
    const more = batch.pendingCount > batch.pendingPreview.length ? `\n  ...and ${batch.pendingCount - batch.pendingPreview.length} more` : ''
    return (
      `Dismiss "${batch.filename || 'Untitled'}" -- ${batch.pendingCount} pending row(s) will stop showing here:\n\n` +
      `${previewLines}${more}\n\n` +
      `Only use this if you've handled them outside the app (e.g. entered manually in Sage) -- if they aren't in Sage yet, they won't auto-sync after this.`
    )
  }

  const handleDismiss = async (batch: SageIncompleteBatch) => {
    if (!window.confirm(buildDismissConfirmMessage(batch))) return
    const reason = window.prompt('Optional note (leave blank to skip):') || undefined
    setDismissingId(batch.id)
    try {
      const result = await dismissBatch(batch, reason)
      if (!result.success) { setParseError(result.error || 'Failed to dismiss batch'); return }
      setIncompleteBatches(await listIncompleteBatches())
    } finally {
      setDismissingId(null)
    }
  }

  // Acknowledge a batch's failed rows so they stop being listed on page load. The rows keep
  // status='error' server-side (audit trail); this only records who/when/why.
  const handleAcknowledgeErrors = async (batch: SageIncompleteBatch) => {
    const lines = batch.errorPreview.map(p => `  Row ${p.row_index + 1} -- ${p.reference || '(no reference)'}`).join('\n')
    const more = batch.errorCount > batch.errorPreview.length ? `\n  ...and ${batch.errorCount - batch.errorPreview.length} more` : ''
    if (!window.confirm(
      `"${batch.filename || 'Untitled'}" -- ${batch.errorCount} invoice(s) were NOT created in Sage:\n\n${lines}${more}\n\n` +
      `Only continue if you have entered them in Sage manually or decided not to pursue them -- this hides the warning, it does not put them in Sage.`
    )) return
    const reason = window.prompt('Optional note (leave blank to skip):') || undefined
    setDismissingId(batch.id)
    try {
      const result = await dismissErrors(batch, reason)
      if (!result.success) { setParseError(result.error || 'Failed to acknowledge failures'); return }
      setIncompleteBatches(await listIncompleteBatches())
    } finally {
      setDismissingId(null)
    }
  }

  const handleDismissAll = async () => {
    const pendingBatches = incompleteBatches.filter(b => b.pendingCount > 0)
    const totalPending = pendingBatches.reduce((sum, b) => sum + b.pendingCount, 0)
    const names = pendingBatches.map(b => `  ${b.filename || b.id} -- ${b.pendingCount} rows`).join('\n')
    const confirmed = window.confirm(
      `Dismiss all ${pendingBatches.length} interrupted batches (${totalPending} pending row(s) total)?\n\n${names}\n\n` +
      `Only use this if you've handled them outside the app -- if they aren't in Sage yet, they won't auto-sync after this.`
    )
    if (!confirmed) return
    const reason = window.prompt('Optional note, applied to all (leave blank to skip):') || undefined
    setDismissingId('__all__')
    try {
      for (const batch of pendingBatches) {
        const result = await dismissBatch(batch, reason)
        if (!result.success) setParseError(`Failed to dismiss ${batch.filename || batch.id}: ${result.error}`)
      }
      setIncompleteBatches(await listIncompleteBatches())
    } finally {
      setDismissingId(null)
    }
  }

  const handleRetryFailed = async () => {
    if (!batchId) return
    setSyncing(true)
    try {
      const result = await retryBatch(batchId)
      if (result.results) {
        // Merge retried results back over the originals by row_index.
        const byIndex = new Map(results.map(r => [r.row_index, r]))
        for (const r of result.results) byIndex.set(r.row_index, r)
        const merged = Array.from(byIndex.values()).sort((a, b) => a.row_index - b.row_index)
        setResults(merged)
        if (merged.some(r => !r.success)) setShowFailureDialog(true)
      }
      if (result.error) setParseError(`Retry stopped early: ${result.error}`)
    } finally {
      setSyncing(false)
    }
  }

  const successResults = results.filter(r => r.success)
  const failResults = results.filter(r => !r.success)
  const successCount = successResults.length
  const failCount = failResults.length
  // Failed rows first so they are the first thing in the list, not buried among the successes.
  const sortedResults = [...failResults, ...successResults]
  // Z73.22: Gary's ask -- "成功 N 条 (发票 X / 贷项 Y) / 失败 M 条".
  const successInvoiceCount = successResults.filter(r => (rows[r.row_index]?.Type || '').trim().toLowerCase() === 'invoice').length
  const successCreditNoteCount = successCount - successInvoiceCount

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="mb-6">
          <Link to="/accounts" className="text-sm text-blue-600 hover:underline">&larr; Back to Accounts</Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mt-2">Bulk Import Invoices to Sage</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Import a BoxTop invoice export CSV (Type / Customer Reference / Date / Customer Name / Reference /
            Ledger Account / Details / Net / VAT Rate / VAT / Total) directly via the API -- required for overseas
            companies, which Sage's own CSV importer doesn't support. An optional "Department" (or "Analysis Code")
            column tags each line with a Sage Department -- pick a default below for rows that don't have one. An
            optional "CustomerRef" column appends a third segment to the Sage reference -- see the "Sage Reference"
            preview column below for exactly what will be sent.
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            Customers outside GB/IE automatically get "VAT Rate" forced to Zero Rated (Sage rejects any other rate
            for them) and Ledger Account 4002 switched to 4004 Overseas Sales -- both highlighted below.
          </p>
        </div>

        {incompleteBatches.length > 0 && results.length === 0 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl shadow p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Earlier imports needing attention -- unfinished rows and invoices that did NOT reach Sage:</p>
              {incompleteBatches.filter(b => b.pendingCount > 0).length > 1 && (
                <button
                  onClick={handleDismissAll}
                  disabled={syncing || dismissingId !== null}
                  className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline disabled:opacity-50"
                >
                  {dismissingId === '__all__' ? 'Dismissing all...' : `Dismiss all (${incompleteBatches.filter(b => b.pendingCount > 0).length})`}
                </button>
              )}
            </div>
            <div className="space-y-2">
              {incompleteBatches.map(b => (
                <div key={b.id} className="text-sm bg-white dark:bg-gray-800 rounded-lg px-3 py-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-gray-700 dark:text-gray-300">
                      {b.filename || 'Untitled'} &mdash; {new Date(b.created_at).toLocaleDateString('en-GB')}
                      {b.pendingCount > 0 && <> &mdash; {b.pendingCount} of {b.total_rows} rows still pending</>}
                      {b.errorCount > 0 && <> &mdash; <span className="text-red-600 dark:text-red-400 font-semibold">{b.errorCount} NOT in Sage</span></>}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {b.pendingCount > 0 && (
                        <button
                          onClick={() => handleResume(b)}
                          disabled={syncing || dismissingId !== null}
                          className="px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-xs font-medium disabled:opacity-50"
                        >
                          Resume
                        </button>
                      )}
                      {b.pendingCount > 0 && (
                        <button
                          onClick={() => handleDismiss(b)}
                          disabled={syncing || dismissingId !== null}
                          className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium disabled:opacity-50"
                        >
                          {dismissingId === b.id ? 'Dismissing...' : 'Dismiss'}
                        </button>
                      )}
                      {b.errorCount > 0 && (
                        <button
                          onClick={() => handleAcknowledgeErrors(b)}
                          disabled={syncing || dismissingId !== null}
                          className="px-3 py-1.5 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium disabled:opacity-50"
                        >
                          {dismissingId === b.id ? 'Saving...' : 'I have handled these'}
                        </button>
                      )}
                    </div>
                  </div>
                  {b.errorCount > 0 && (
                    <ul className="mt-2 space-y-1 text-xs text-red-600 dark:text-red-400">
                      {b.errorPreview.map(p => (
                        <li key={p.row_index} title={p.error_msg}>
                          <span className="font-mono font-semibold">{p.reference || `Row ${p.row_index + 1}`}</span> &mdash; {explainSageError(p.error_msg)}
                        </li>
                      ))}
                      {b.errorCount > b.errorPreview.length && <li>...and {b.errorCount - b.errorPreview.length} more</li>}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {progress && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 mb-6">
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
              Syncing batch {progress.chunkIndex}/{progress.totalChunks} &middot; {progress.processedRows}/{progress.totalRows} rows done
            </p>
            <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${progress.totalRows > 0 ? Math.round((progress.processedRows / progress.totalRows) * 100) : 0}%` }}
              />
            </div>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 mb-6">
          <label className="block">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CSV file</span>
            <input
              type="file"
              accept=".csv"
              onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="mt-2 block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white file:font-medium hover:file:bg-blue-700"
            />
          </label>
          {parseError && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{parseError}</p>}

          {departmentCategories.length > 0 && (
            <label className="block mt-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Default Department for this upload</span>
              <select
                value={defaultDepartment}
                onChange={e => setDefaultDepartment(e.target.value)}
                className="mt-2 w-full sm:w-80 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg px-3 py-2"
              >
                <option value="">— No default (only rows with a Department column value get tagged) —</option>
                {departmentCategories.map(d => (
                  <option key={d.code} value={d.code}>{d.code} — {d.name}</option>
                ))}
              </select>
              <span className="block mt-1 text-xs text-gray-500 dark:text-gray-400">
                Applied only to rows whose CSV "Department"/"Analysis Code" column is blank -- a row that already has a value keeps it.
              </span>
            </label>
          )}
        </div>

        {rows.length > 0 && results.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {rows.length} row{rows.length === 1 ? '' : 's'} ready to sync
                {skippedCount > 0 && ` · ${skippedCount} row${skippedCount === 1 ? '' : 's'} skipped (no Type)`}
              </p>
              <button
                onClick={handleSyncAll}
                disabled={syncing}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {syncing ? 'Syncing…' : `Sync All ${rows.length} to Sage`}
              </button>
            </div>
            <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                  <tr>
                    {[...EXPECTED_HEADERS, 'Department', 'CustomerRef', 'Sage Reference', ''].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {effectiveRows.map((row, idx) => {
                    const isOverseas = !!overseasByReference[row['Customer Reference']]
                    return (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        {EXPECTED_HEADERS.map(h => {
                          const isConverted = isOverseas && (h === 'VAT Rate' || (h === 'Ledger Account' && !!OVERSEAS_LEDGER_ACCOUNT_OVERRIDE[(row['Ledger Account'] || '').trim()]))
                          return (
                            <td
                              key={h}
                              className={`px-3 py-2 whitespace-nowrap ${isConverted ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                              title={isConverted ? 'Overridden -- customer is outside GB/IE' : undefined}
                            >
                              {h === 'VAT Rate' ? vatRateDisplay(row, isOverseas)
                                : h === 'Ledger Account' ? ledgerAccountDisplay(row, isOverseas)
                                : String(row[h] ?? '')}
                            </td>
                          )
                        })}
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.Department || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{row.CustomerRef || '—'}</td>
                        <td className="px-3 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-white" title="What will actually be sent as the Sage invoice's Reference field">
                          {buildSageReference(row.Reference, row.Details, row.CustomerRef) ?? '—'}
                        </td>
                        <td className="px-3 py-2">
                          <button onClick={() => removeRow(idx)} className="text-red-500 hover:text-red-700 text-xs">Remove</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {results.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <span className="text-green-600 dark:text-green-400 font-semibold">
                  Succeeded: {successCount} (invoices {successInvoiceCount} / credit notes {successCreditNoteCount})
                </span>
                {failCount > 0 && <> · <span className="text-red-600 dark:text-red-400 font-semibold">Failed: {failCount}</span></>}
              </p>
              <div className="flex gap-2">
                {failCount > 0 && (
                  <button
                    onClick={() => exportFailuresCsv(results, rows, filename || undefined)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                  >
                    Export Failures CSV
                  </button>
                )}
                {failCount > 0 && (
                  <button
                    onClick={handleRetryFailed}
                    disabled={syncing}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium disabled:opacity-50"
                  >
                    {syncing ? 'Retrying…' : `Retry ${failCount} Failed`}
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-[28rem] overflow-y-auto">
              {sortedResults.map(r => (
                <div key={r.row_index} className={`px-6 py-3 flex items-center justify-between gap-4 text-sm ${r.success ? '' : 'bg-red-50 dark:bg-red-900/20'}`}>
                  <div>
                    <span className="font-mono text-gray-500 dark:text-gray-400">Row {r.row_index + 1}</span>
                    {' — '}
                    <span className="text-gray-700 dark:text-gray-300">
                      {rows[r.row_index]?.Reference || rows[r.row_index]?.['Customer Name'] || '—'}
                    </span>
                  </div>
                  {r.success ? (
                    <span className="text-green-600 dark:text-green-400 font-medium">✓ Synced ({r.sageInvoiceId})</span>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 text-xs text-right max-w-md" title={r.error}>✗ {explainSageError(r.error)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showFailureDialog && failCount > 0 && (
        <SageFailureDialog
          failCount={failCount}
          total={results.length}
          successCount={successCount}
          noun="invoice"
          items={failResults.map(r => {
            const row = rows[r.row_index]
            return {
              key: r.row_index,
              ref: row?.Reference || `Row ${r.row_index + 1}`,
              party: row?.['Customer Name'] || row?.['Customer Reference'] || '',
              detail: row ? `Net ${row.Net} · ${row['VAT Rate'] || 'no VAT rate'} · Ledger ${row['Ledger Account']}` : undefined,
              error: r.error,
            }
          })}
          onExport={() => exportFailuresCsv(results, rows, filename || undefined)}
          onClose={() => setShowFailureDialog(false)}
        />
      )}
    </div>
  )
}
